<template>
  <div class="container-fluid">

    <PageHeader showBreadcrumbs page-title="Finance reports - profit and loss historical">
      <template #buttons>
        <b-form-group class="btn-icon button-checkbox_sm w-100">
          <b-form-checkbox
            size="sm"
            switch
            v-model="showLastMonth"
            class="switch"
            >Show last month</b-form-checkbox
          >
        </b-form-group>
      </template>
    </PageHeader>

    <div ref="tableContain" class="profit-historical round">
      <div v-if="isDataPageLoading" class="content-loading-wrapper" style="min-height: 60vh;">
        <Preloader class="centered-preloader large-spinner" />
      </div>
      <HistoricalReportTable
        v-else-if="Object.keys(reportData).length"
        class="pl-report-historical table__theme-default slideout__table position-relative"
        :fixed="false"
        hover
        responsive
      >
        <b-thead
          head-variant="light"
          :class="{'sticky-head': getCoordinates > 0}"
          :style="{'top': getCoordinates + 'px'}"
        >
          <b-tr class="row-top" style="padding: 0; height: 27px;">
            <b-th class="date-column" :style="{'width': thsWidth[`th_date`] + 'px'}" sticky-column>date</b-th>
            <b-th :style="{'width': thsWidth[`th_revenue`] + 'px'}" sticky-column>revenue</b-th>
            <b-th class="border-right-0" :style="{'width': thsWidth[`th_payouts`] + 'px'}" sticky-column>payouts</b-th>
            <b-th class="border-left-0" :style="{'width': thsWidth[`th_expenses`] + 'px'}" sticky-column>expenses</b-th>
            <b-th class="border-right-0" :style="{'width': thsWidth[`th_profit`] + 'px'}" sticky-column>profit</b-th>
            <b-th class="border-left-0" :style="{'width': thsWidth[`th_profit_margin`] + 'px'}" sticky-column>profit margin</b-th>
            <b-th :style="{'width': thsWidth[`th_monthly_profit`] + 'px'}" sticky-column>monthly profit</b-th>
            <b-th sticky-column>monthly revenue</b-th>
            <b-th />
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr class="all-time-row">
            <b-td class="date-column font-weight-bold">All time</b-td>
            <b-td>{{ reportData.summary.revenue }}</b-td>
            <b-td class="border-right-0">{{ reportData.summary.payouts }}</b-td>
            <b-td class="border-left-0">{{ reportData.summary.expenses }}</b-td>
            <b-td class="border-right-0">{{ reportData.summary.profit }}</b-td>
            <b-td class="border-left-0">
              <span
                :class="{
                  'text-red': reportData.summary.profit_margin <= 0,
                  'text-green': reportData.summary.profit_margin > 0,
                }">{{ reportData.summary.profit_margin }}%</span>
            </b-td>
            <b-td>{{ reportData.summary.monthly_profit }}</b-td>
            <b-td>{{ reportData.summary.monthly_revenue }}</b-td>
            <b-td class="border-right-0" />
          </b-tr>
          <b-tr class="empty-row"></b-tr>
          <template v-for="(yearData, i) in reportData.data">
            <b-tr
              class="group-border"
              :class="{'expanded-year': expandedYear === yearData.year}"
              v-if="yearData.year"
              :key="'year-' + yearData.year + '-' + i"
            >
              <b-td class="date-column font-weight-bold">{{ yearData.year }}</b-td>
              <b-td>{{ yearData.summary.revenue }}</b-td>
              <b-td>{{ yearData.summary.payouts }}</b-td>
              <b-td>{{ yearData.summary.expenses }}</b-td>
              <b-td>{{ yearData.summary.profit }}</b-td>
              <b-td>
                <span
                  :class="{
                    'text-red': yearData.summary.profit_margin <= 0,
                    'text-green': yearData.summary.profit_margin > 0,
                  }"
                  class="profit-margin-cell"
                  >{{ yearData.summary.profit_margin }}%</span
                >
              </b-td>
              <b-td>{{ yearData.summary.monthly_profit }}</b-td>
              <b-td>{{ yearData.summary.monthly_revenue }}</b-td>
              <b-td class="arrow-icon">
                <span
                  class="material-symbols-outlined cursor-poiner"
                  @click="toggleYear(yearData.year)"
                >
                  {{
                    expandedYear === yearData.year
                      ? "keyboard_arrow_up"
                      : "keyboard_arrow_down"
                  }}
                </span>
              </b-td>
            </b-tr>
            <template v-if="expandedYear !== yearData.year">
              <b-tr :key="i" class="empty-row"></b-tr>
            </template>
            <template v-else>
              <b-tr
                class="month-row"
                v-for="(monthData, index) in yearData.data"
                :key="monthData.month + '-' + index"
                 :class="{
                    'month-row-first-in-group': index === 0,
                    'month-row-last-in-group': index === yearData.data.length - 1,
                  }"
              >
                <b-td class="date-column">{{ monthLabel(monthData.month) }}</b-td>
                <b-td>{{ monthData.data.revenue }}</b-td>
                <b-td class="border-right-0">{{ monthData.data.payouts }}</b-td>
                <b-td class="border-left-0">{{ monthData.data.expenses }}</b-td>
                <b-td class="border-right-0">{{ monthData.data.profit }}</b-td>
                <b-td class="border-left-0">
                  <span
                    :class="{
                      'text-red': monthData.data.profit_margin <= 0,
                      'text-green': monthData.data.profit_margin > 0,
                    }"
                    class="profit-margin-cell">{{ monthData.data.profit_margin }}%</span>
                </b-td>
                <b-td> 0 </b-td>
                <b-td class="border-right-0"> 0 </b-td>
                <b-td class="border-0"></b-td>
              </b-tr>
              <b-tr :key="i" class="empty-row"></b-tr>
            </template>
          </template>
        </b-tbody>
      </HistoricalReportTable>
    </div>
    <div v-if="!isDataPageLoading">
      <h3 class="title">Charts</h3>
    </div>
    <div v-if="!isDataPageLoading" class="round charts-container">
      <fullscreen
        background="#F3F5F7"
        ref="fullscreenBarChart"
        @change="fullScreenChange"
        class=" p-0 charts-w"
      >
        <div class="py-3 pr-4 pl-2 charts-bg">
          <b-button
            class="button btn-icon fullscreen-button"
            variant="outline-info"
            @click="toggleFullscreen('fullscreenBarChart')">
            <span class="material-icons">fullscreen</span>
          </b-button>
        <canvas id="barChart">Your browser does not support the canvas element.</canvas>
        </div>
      </fullscreen>
      <fullscreen
        background="#F3F5F7"
        ref="fullscreenProfitMarginChart"
        @change="fullScreenChange"
        class="p-0 charts-w"
      >
        <div class="py-3 pr-4 pl-2 charts-bg">
          <b-button
            class="button btn-icon fullscreen-button"
            variant="outline-info"
            @click="toggleFullscreen('fullscreenProfitMarginChart')">
            <span class="material-icons">fullscreen</span>
          </b-button>
          <canvas id="profitMarginChart">Your browser does not support the canvas element.</canvas>
        </div>
      </fullscreen>
      <fullscreen
        background="#F3F5F7"
         ref="fullscreenRevenueProfitChart"
        @change="fullScreenChange"
        class="p-0 charts-w"
      >
        <div class="py-3 pr-4 pl-2 charts-bg">
            <b-button
              class="button btn-icon fullscreen-button"
              variant="outline-info"
              @click="toggleFullscreen('fullscreenRevenueProfitChart')">
              <span class="material-icons">fullscreen</span>
            </b-button>
          <canvas id="revenueProfitChart">Your browser does not support the canvas element.</canvas>
        </div>
      </fullscreen>
    </div>
  </div>
</template>

<script>
import { permissions } from '@mocks/permissions'
import StickyHeaderMixin from '../mixins/StickyHeaderMixin'
import Chart from 'chart.js'
import Preloader from '@components/Preloader'
import HistoricalReportTable from '@components/finance/HistoricalReportTable.vue'
import PageHeader from '@components/PageHeader.vue'

export default {
  name: 'FinanceReportsProfitAndLossHistorical',
  mixins: [ StickyHeaderMixin ],
  components: {
    Preloader,
    HistoricalReportTable,
    PageHeader
  },
  data () {
    return {
      permissions: permissions,
      showLastMonth: false,
      reportData: {
        'data': [],
        'summary': {}
      },
      barChart: null,
      expandedYear: null,
      profitMarginChart: null,
      revenueProfitChart: null
    }
  },
  computed: {
    getDataByMonths () {
      const monthData = []
      const reportData = this.reportData.data
      for (const yi in reportData) {
        const year = reportData[yi].year
        const yearData = reportData[yi].data
        for (const mi in yearData) {
          const data = yearData[mi].data
          monthData.push({ month: yearData[mi].month, year, data })
        }
      }
      return monthData.reverse()
    }
  },
  watch: {
    async showLastMonth (value) {
      await this.fetchReportData(value)
      this.barChart.destroy()
      this.profitMarginChart.destroy()
      this.revenueProfitChart.destroy()
      this.createCharts()
    }
  },
  async created () {
    await this.fetchReportData(this.showLastMonth)
    this.createCharts()
  },
  methods: {
    async fetchReportData (showLastMonth = false) {
      return this._toggleDataPageLoading(async () => {
        await this.$api.finances
          .getReportsProfitAndLossHistorical(showLastMonth)
          .then((response) => {
            this.reportData = response.data
          })
      })
    },
    fullScreenChange (fullscreen) {
      this.fullscreen = fullscreen
    },
    toggleFullscreen (refName) {
      const fullscreenRef = this.$refs[refName]
      if (fullscreenRef && typeof fullscreenRef.toggle === 'function') {
        fullscreenRef.toggle()
      }
    },
    monthLabel (dateString) {
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ]

      return monthNames[parseInt(dateString, 10) - 1]
    },
    toggleYear (year) {
      this.expandedYear = this.expandedYear === year ? null : year
    },
    createCharts () {
      const barChartCont = document.getElementById('barChart').getContext('2d')

      const monthsLabels = this.getDataByMonths.map(monthData =>
        new Date(`${monthData.year}-${monthData.month}`).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      )

      const payouts = this.getDataByMonths.map(monthData => monthData.data['payouts'] ?? 0)
      const expenses = this.getDataByMonths.map(monthData => monthData.data['expenses'] ?? 0)
      const profit = this.getDataByMonths.map(monthData => monthData.data['profit'] ?? 0)

      this.barChart = new Chart(barChartCont, {
        type: 'bar',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Payouts',
              data: payouts,
              stack: 'month',
              backgroundColor: '#2F80ED',
              borderColor: '#2F80ED',
              borderWidth: 1
            },
            {
              label: 'Expenses',
              data: expenses,
              stack: 'month',
              backgroundColor: '#FFB401',
              borderColor: '#FFB401',
              borderWidth: 1
            },
            {
              label: 'Profit',
              data: profit,
              stack: 'month',
              backgroundColor: '#E138B9',
              borderColor: '#E138B9',
              borderWidth: 1
            }
          ]
        },
        options: {
          legend: {
            position: 'top',
            labels: {
              fontSize: 12,
              usePointStyle: true
            }
          },
          scales: {
            xAxes: [{
              ticks: {
                fontSize: 8,
                fontColor: '#4F4F4F'
              }
            }]
          }
        }
      })

      const profitMarginChartCont = document.getElementById('profitMarginChart').getContext('2d')

      const profitMargin = this.getDataByMonths.map(monthData => monthData.data['profit_margin'] ?? 0)

      this.profitMarginChart = new Chart(profitMarginChartCont, {
        type: 'line',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Profit margin',
              data: profitMargin,
              fill: false,
              borderColor: '#FFB401',
              borderWidth: 3,
              backgroundColor: '#FFB401',
              lineTension: 0
            }
          ]
        },
        options: {
          legend: {
            position: 'top',
            labels: {
              fontSize: 12,
              usePointStyle: true
            }
          },
          scales: {
            xAxes: [{
              ticks: {
                fontSize: 8,
                fontColor: '#4F4F4F'
              }
            }]
          }
        }
      })

      const revenueProfitChartCont = document.getElementById('revenueProfitChart').getContext('2d')

      const revenue = this.getDataByMonths.map(monthData => monthData.data['revenue'] ?? 0)

      this.revenueProfitChart = new Chart(revenueProfitChartCont, {
        type: 'line',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Profit',
              data: profit,
              fill: false,
              borderColor: '#FFB401',
              backgroundColor: '#FFB401',
              borderWidth: 3,
              lineTension: 0
            },
            {
              label: 'Revenue',
              data: revenue,
              fill: false,
              borderColor: '#2F80ED',
              backgroundColor: '#2F80ED',
              borderWidth: 3,
              lineTension: 0
            }
          ]
        },
        options: {
          legend: {
            position: 'top',
            labels: {
              fontSize: 12,
              usePointStyle: true
            }
          },
          scales: {
            xAxes: [{
              ticks: {
                fontSize: 8,
                fontColor: '#4F4F4F'
              }
            }]
          }
        }

      })
    }
  }
}

</script>

<style lang="scss" scoped>
.pl-report-historical {
  > ::v-deep table {
    max-width: 1200px;
  }

  thead tr th {
    &::before {
      display: none !important;
    }
  }

  .text-green {
    color: $notifySuccessColor;
  }

  .text-red {
    color: $notifyErrColor;
  }

  .profit-margin-cell {
    min-width: 90px;
    display: inline-block;
  }
}

.profit-report-chart-title {
  font-size: 22px;
  text-align: left;
  font-weight: 500;
  margin-left: 10px;
  margin-bottom: 10px;
}

.charts-bg {
  border-radius: 10px;
  background-color: white;
  position: relative;
}

.charts-container {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  gap: 20px;
}

.charts-w {
  width: 49%;
}

.fullscreen-button {
  position: absolute;
  top: 10px;
  right: 18px;
}

</style>
